import { Component, OnInit, HostListener, ChangeDetectionStrategy, TrackByFunction } from '@angular/core';
import { SummaryCard, PieChartData, LineChartData, BarChartData, PendingVote } from './models/dashboard.models';
import { TableColumn } from 'src/app/shared/components/data-table/data-table.component';
import { Chart, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(...registerables, ChartDataLabels);

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeComponent implements OnInit {
  
  // TrackBy functions for better performance
  trackByCardIndex: TrackByFunction<SummaryCard> = (index: number) => index;
  trackByVoteTenderId: TrackByFunction<PendingVote> = (index: number, item: PendingVote) => item.tenderId;

  // Summary Cards Data
  summaryCards: SummaryCard[] = [];

  // Chart Data
  tenderStatusChartData: PieChartData | null = null;
  evaluationProgressChartData: PieChartData | null = null;
  monthlyTrendChartData: LineChartData | null = null;
  departmentTendersChartData: BarChartData | null = null;
  budgetChartData: BarChartData | null = null;

  // Table Data
  pendingVotes: PendingVote[] = [];
  tableColumns: TableColumn[] = [];

  // ========================================================================
  // CHART STYLES & CONFIGURATION
  // These are defined separately so they persist even when data is dynamic
  // ========================================================================

  // 1. Colors Configuration
  readonly chartColors = {
    purple: '#532E60',
    pink: '#EC4899',
    orange: '#FFA500',
    yellow: '#FBBF24',
    red: '#EF4444',
    green: '#10B981',
    text: '#333843',
    grid: '#F0F1F3',
    textLight: '#667085'
  };

  // 2. Pie Chart Options (Tender Status, Evaluation Progress)
  pieChartOptions: any;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.updateChartOptions();
  }

  private updateChartOptions() {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 1243; // Updated breakpoint per user request

    this.pieChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: isMobile ? 10 : 30
      },
      plugins: {
        legend: {
          display: isMobile, // Show legend ONLY on mobile
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: { size: 12 },
            color: this.chartColors.text
          }
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const label = context.label || '';
              const value = context.parsed || 0;
              return `${label}: ${value}%`;
            }
          },
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          titleFont: { size: 14 },
          bodyFont: { size: 12 }
        },
        datalabels: {
          display: !isMobile, // Show datalabels ONLY on desktop
          color: (context: any) => {
            // Get the color from the dataset's backgroundColor array
            const colors = context.chart.data.datasets[0].backgroundColor;
            return colors[context.dataIndex];
          },
          font: {
            size: 14,
            weight: '600' as const
          },
          formatter: (value: number, context: any) => {
            const label = context.chart.data.labels[context.dataIndex];
            const total = context.chart.data.datasets[0].data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(0);
            return `${label} ${percentage}%`;
          },
          anchor: 'end' as const,
          align: 'end' as const,
          offset: 15,
          clamp: true,
          clip: false
        }
      }
    };
  }

  // 3. Line Chart Options (Monthly Trends)
  readonly lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 12, weight: 'normal' },
          color: this.chartColors.text
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12
      },
      datalabels: {
        display: true,
        color: this.chartColors.text,
        font: {
          size: 11,
          weight: '600' as const
        },
        formatter: (value: number) => value,
        anchor: 'end' as const,
        align: 'top' as const,
        offset: 0,
        clamp: true
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { size: 11 },
          color: this.chartColors.textLight
        }
      },
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          stepSize: 25,
          font: { size: 11 },
          color: this.chartColors.textLight
        },
        grid: { color: this.chartColors.grid }
      }
    }
  };

  // 4. Bar Chart Options - Common base configuration
  private getBaseBarChartOptions() {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: { size: 12, weight: 'normal' },
            color: this.chartColors.text
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: 12,
          callbacks: {
            label: (context: any) => {
              let label = context.dataset.label || '';
              if (label) label += ': ';
              if (context.parsed.y !== null) {
                if (context.parsed.y > 1000) {
                  label += new Intl.NumberFormat('en-US').format(context.parsed.y);
                } else {
                  label += context.parsed.y;
                }
              }
              return label;
            }
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            font: { size: 11 },
            color: this.chartColors.textLight
          }
        },
        y: {
          beginAtZero: true,
          grid: {
            color: this.chartColors.grid,
            drawBorder: false
          },
          ticks: {
            font: { size: 11 },
            color: this.chartColors.textLight,
            callback: (value: any) => {
              if (value >= 1000) {
                return new Intl.NumberFormat('en-US', { notation: 'compact' }).format(value);
              }
              return value;
            }
          }
        }
      }
    };
  }

  // Department Tenders Bar Chart Options (horizontal labels)
  get departmentBarChartOptions() {
    return {
      ...this.getBaseBarChartOptions(),
      plugins: {
        ...this.getBaseBarChartOptions().plugins,
        datalabels: {
          display: true,
          color: '#ffffff',
          font: {
            size: 11,
            weight: '600' as const
          },
          formatter: (value: number) => value,
          anchor: 'center' as const,
          align: 'center' as const,
          clamp: true
        }
      }
    };
  }

  // Budget Bar Chart Options (vertical labels)
  get budgetBarChartOptions() {
    return {
      ...this.getBaseBarChartOptions(),
      plugins: {
        ...this.getBaseBarChartOptions().plugins,
        datalabels: {
          display: true,
          color: '#ffffff',
          font: {
            size: 11,
            weight: '600' as const
          },
          formatter: (value: number) => {
            // Format large numbers with compact notation
            if (value >= 1000) {
              return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
            }
            return value;
          },
          anchor: 'center' as const,
          align: 'center' as const,
          rotation: -90,
          clamp: true
        }
      }
    };
  }

  ngOnInit(): void {
    // No need to initialize options here anymore as they are readonly properties
    this.initializeTableColumns();
    this.updateChartOptions(); // Initialize chart options based on current screen size
    this.loadDashboardData();
  }

  getChartHeight(): number {
    if (typeof window !== 'undefined') {
      if (window.innerWidth <= 576) {
        return 280;
      } else if (window.innerWidth <= 768) {
        return 320;
      }
    }
    return 400;
  }

  private initializeTableColumns(): void {
    this.tableColumns = [
      {
        field: 'tenderId',
        header: 'Tender ID',
        type: 'text'
      },
      {
        field: 'title',
        header: 'Title',
        type: 'text'
      },
      {
        field: 'value',
        header: 'Value (QAR)',
        type: 'number',
        format: '1.0-0'
      },
      {
        field: 'deadline',
        header: 'Deadline',
        type: 'date',
        format: 'dd/MM/yyyy'
      },
      {
        field: 'status',
        header: 'Status',
        type: 'badge',
        badgeClass: 'status-badge'
      }
    ];
  }

  private loadDashboardData(): void {
    // Summary Cards
    this.summaryCards = [
      { title: 'Total Tenders', value: 24, icon: 'bi-file-earmark-text', iconColor: '#FF6B9D' },
      { title: 'Pending Evaluations', value: 8, icon: 'bi-graph-up-arrow', iconColor: this.chartColors.orange },
      { title: 'Active Meetings', value: 3, icon: 'bi-people', iconColor: this.chartColors.purple },
      { title: 'Open Actions', value: 12, icon: 'bi-clipboard-check', iconColor: this.chartColors.green },
      { title: 'Total Minutes', value: 8, icon: 'bi-calendar', iconColor: this.chartColors.purple },
      { title: 'Pending Review', value: 2, icon: 'bi-circle-fill', iconColor: this.chartColors.yellow }
    ];

    // Tender Status Distribution (Pie)
    this.tenderStatusChartData = {
      labels: ['Draft', 'Rejected', 'Awarded', 'Evaluation', 'Published'],
      datasets: [{
        data: [17, 4, 21, 23, 35],
        backgroundColor: [
          this.chartColors.purple,
          this.chartColors.red,
          this.chartColors.yellow,
          this.chartColors.orange,
          this.chartColors.pink
        ],
        borderColor: [
          this.chartColors.purple,
          this.chartColors.red,
          this.chartColors.yellow,
          this.chartColors.orange,
          this.chartColors.pink
        ],
        borderWidth: 0
      }]
    };

    // Evaluation Progress (Pie)
    this.evaluationProgressChartData = {
      labels: ['Completed', 'Pending', 'In Progress'],
      datasets: [{
        data: [50, 17, 33],
        backgroundColor: [
          this.chartColors.yellow,
          this.chartColors.orange,
          this.chartColors.pink
        ],
        borderColor: [
          this.chartColors.yellow,
          this.chartColors.orange,
          this.chartColors.pink
        ],
        borderWidth: 0
      }]
    };

    // Monthly Trends (Line)
    this.monthlyTrendChartData = {
      labels: ['Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov'],
      datasets: [
        {
          label: 'Bids',
          data: [40, 50, 60, 70, 85, 75],
          borderColor: this.chartColors.red,
          backgroundColor: 'transparent',
          tension: 0.4,
          fill: false,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6
        },
        {
          label: 'Tenders',
          data: [25, 28, 30, 32, 35, 30],
          borderColor: this.chartColors.purple,
          backgroundColor: 'transparent',
          tension: 0.4,
          fill: false,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6
        }
      ]
    };

    // Department Tenders (Bar)
    this.departmentTendersChartData = {
      labels: ['IT', 'Procurement', 'Finance', 'Security', 'HR'],
      datasets: [{
        label: 'Tenders',
        data: [7, 5.5, 4.5, 3, 2],
        backgroundColor: this.chartColors.purple,
        borderColor: this.chartColors.purple,
        borderWidth: 0,
        borderRadius: 4
      }]
    };

    // Budget Allocation (Bar)
    this.budgetChartData = {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov'],
      datasets: [
        {
          label: 'Allocated',
          data: [200000, 180000, 220000, 190000, 210000, 240000, 230000, 250000, 270000, 290000, 280000],
          backgroundColor: this.chartColors.purple,
          borderColor: this.chartColors.purple,
          borderWidth: 0,
          borderRadius: 4
        },
        {
          label: 'Spent',
          data: [180000, 170000, 200000, 185000, 200000, 220000, 210000, 230000, 250000, 270000, 260000],
          backgroundColor: this.chartColors.pink,
          borderColor: this.chartColors.pink,
          borderWidth: 0,
          borderRadius: 4
        }
      ]
    };

    // Pending Votes Table
    this.pendingVotes = [
      {
        tenderId: 'T-000001',
        title: 'IT Infrastructure Upgrade',
        value: 450000,
        deadline: new Date('2025-11-20'),
        status: 'Awaiting Vote'
      },
      {
        tenderId: 'T-000002',
        title: 'Office Furniture Procurement',
        value: 125000,
        deadline: new Date('2025-11-18'),
        status: 'Awaiting Vote'
      },
      {
        tenderId: 'T-000003',
        title: 'Security Systems Installation',
        value: 280000,
        deadline: new Date('2025-11-22'),
        status: 'Awaiting Vote'
      }
    ];
  }

  onTableActionClick(event: { action: string, row: any }): void {
    // Handle table action clicks
    console.log('Action clicked:', event);
  }

  onTableLinkClick(event: { field: string, row: any }): void {
    // Handle table link clicks
    console.log('Link clicked:', event);
  }
}
